import { useEffect } from 'react'
import { trackEvent } from '../utils/mixpanel'

const PrivacyPolicy = () => {
  useEffect(() => { trackEvent('Page Viewed', { page: 'Privacy Policy' }) }, [])

  return (
    <div className="bg-white min-h-screen">
      <div className="container mx-auto px-4 py-16 max-w-3xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-10">Last updated: June 2, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p>
              Wellness Loop ("we", "us", or "our") operates the wellnessloop.in website and
              related services (collectively, the "Platform"). This Privacy Policy explains how
              we collect, use, disclose, and safeguard your personal information when you use
              our Platform.
            </p>
            <p>
              By accessing or using our Platform, you agree to the terms of this Privacy Policy.
              If you do not agree, please discontinue use of the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email address, and phone number when you create an account or register for events</li>
              <li>Profile information such as wellness interests, preferences, and goals</li>
              <li>Payment and billing details when you purchase events, programs, or services</li>
              <li>Feedback, testimonials, check-in responses, and survey answers you submit</li>
              <li>Communications you send to us (e.g., support requests, inquiries)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>Log data (IP address, access times, pages viewed, referring URLs)</li>
              <li>Usage analytics through tools such as Mixpanel and Google Analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">2.3 Information from Third Parties</h3>
            <p>
              We may receive information from third-party services you use to log in (e.g.,
              Google) or from payment processors (e.g., Razorpay) to complete transactions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To create and manage your account</li>
              <li>To facilitate event registrations, bookings, and program enrollments</li>
              <li>To process payments and send transaction confirmations</li>
              <li>To personalize your experience and recommend relevant wellness content</li>
              <li>To communicate with you about events, updates, and promotions</li>
              <li>To improve and maintain the Platform's functionality and security</li>
              <li>To analyze usage trends and optimize our services</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. Sharing of Information</h2>
            <p>We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Facilitators and event hosts</strong> — your name, email, and relevant
                registration details so they can manage sessions you've signed up for
              </li>
              <li>
                <strong>Service providers</strong> — third-party vendors who assist with payment
                processing (Razorpay), email delivery, analytics, and hosting
              </li>
              <li>
                <strong>Legal requirements</strong> — when required by law, court order, or
                governmental authority
              </li>
              <li>
                <strong>Business transfers</strong> — in connection with a merger, acquisition,
                or sale of assets, your information may be transferred
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to enhance your experience, analyze
              traffic, and serve relevant content. You can manage your cookie preferences
              through your browser settings. Disabling cookies may affect certain features of
              the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. Data Security</h2>
            <p>
              We implement reasonable technical and organizational measures to protect your
              personal information against unauthorized access, alteration, disclosure, or
              destruction. However, no method of electronic transmission or storage is 100%
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as
              needed to provide you services. We may also retain information as required to
              comply with legal obligations, resolve disputes, and enforce our agreements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate or incomplete information</li>
              <li>Request deletion of your personal information, subject to legal obligations</li>
              <li>Withdraw consent for marketing communications at any time</li>
              <li>Object to or restrict certain processing of your information</li>
            </ul>
            <p className="mt-2">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:hello@wellnessloop.in" className="text-indigo-600 hover:text-indigo-800 underline">
                hello@wellnessloop.in
              </a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">9. Third-Party Links</h2>
            <p>
              Our Platform may contain links to third-party websites or services. We are not
              responsible for the privacy practices of these external sites. We encourage you to
              read the privacy policies of any third-party services you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. Children's Privacy</h2>
            <p>
              Our Platform is not intended for individuals under the age of 18. We do not
              knowingly collect personal information from children. If we learn that we have
              collected information from a child under 18, we will take steps to delete it
              promptly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of
              significant changes by posting the updated policy on the Platform with a revised
              "Last updated" date. Your continued use of the Platform after changes are posted
              constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Wellness Loop</strong><br />
              Email:{' '}
              <a href="mailto:hello@wellnessloop.in" className="text-indigo-600 hover:text-indigo-800 underline">
                hello@wellnessloop.in
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
